import { Accordion, AccordionSummary, Switch, Typography } from '@mui/material';
import { Box } from '@mui/system';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { GenericProductDto, PricingQuoteDetailsDto, PricingQuoteIncludedProductDto, ProductBillingTypeEnum, ProductTypeEnum } from '@inosio/payments-sdk';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { calculateCostFromTiers } from '../../../shared/util/calculators';
import { buildPricingQuoteDto, selectOnboardingApplicationState } from '../../../pmui/onboarding/sagas/pmonboardingSlice';
import { ProductPreferencesProps } from '../../interfaces/ProductPreferenceInterface';

const ApplicationPreferences = (props: ProductPreferencesProps) => {
  const dispatch = useDispatch();
  const { PricingQuoteSetupObj } = useSelector(selectOnboardingApplicationState);
  const availableApplicationProducts = props.availableProducts as GenericProductDto[];
  // Filter the availableapplication products based on their billing type
  const consumableApplicationProduct = availableApplicationProducts.find((product) => product.ProductBillingType === ProductBillingTypeEnum.PerConsumption);
  const flatFeeApplicationProduct = availableApplicationProducts.find((product) => product.ProductBillingType === ProductBillingTypeEnum.FlatMonthlyFee);
  //fetch these products from our master pricing quote obj if any
  const selectedConsumableApplicationProduct = PricingQuoteSetupObj.IncludedProducts.find((product) => product.ProductUuid === consumableApplicationProduct?.ProductUuid);
  const selectedFlatFeeApplicationProduct = PricingQuoteSetupObj.IncludedProducts.find((product) => product.ProductUuid === flatFeeApplicationProduct?.ProductUuid);

  //local states
  const [isApplicationsSelected, setIsApplicationsSelected] = useState(!!selectedConsumableApplicationProduct && !!selectedFlatFeeApplicationProduct);

  //useEffects

  /**
   * This useffect listens for changes in the isApplicationsSelected (whether product is removed or added) state.
   * It then makes changes to the PricingQuoteSetupObj based on this state.
   */
  useEffect(() => {
    const existingSelectedProducts = PricingQuoteSetupObj.IncludedProducts || [];

    let updatedSelectedProducts = [...existingSelectedProducts];

    if (isApplicationsSelected) {
      //if the prerequisite products are not on then turn them on first
      props.preRequisiteProducts.forEach((prereq) => {
        const existingProduct = PricingQuoteSetupObj.IncludedProducts.find((prod) => prod.ProductUuid === prereq.ProductUuid && prod.ProductType === prereq.ProductType);
        if (!existingProduct) {
          updatedSelectedProducts.push({
            ProductUuid: prereq.ProductUuid,
            ProductType: prereq.ProductType,
          } as PricingQuoteIncludedProductDto);
        }
      });

      // Add consumable application product if available
      if (consumableApplicationProduct) {
        const consumableProduct: PricingQuoteIncludedProductDto = {
          ProductUuid: consumableApplicationProduct.ProductUuid,
          ProductType: ProductTypeEnum.Applications,
          ConsumableFee: consumableApplicationProduct.ConsumableFee,
          ProductLabel: 'Application Consumable',
        };

        //first check if its already been selected, if yes then replace it
        if (!!selectedConsumableApplicationProduct) {
          // Replace the existing product with the selected one
          updatedSelectedProducts = existingSelectedProducts.map((prod) => (prod.ProductUuid === consumableProduct.ProductUuid ? consumableProduct : prod));
        } else {
          // Add the selected product
          updatedSelectedProducts.push(consumableProduct);
        }
      }

      // Add flat fee application product if available
      if (flatFeeApplicationProduct) {
        const flatFeeProduct: PricingQuoteIncludedProductDto = {
          ProductUuid: flatFeeApplicationProduct.ProductUuid,
          ProductType: ProductTypeEnum.Applications,
          FlatFee: calculateCostFromTiers(flatFeeApplicationProduct.FlatFeeTiers || [], props.doorCount || 0),
          ProductLabel: 'Application Portal',
        };
        //first check if its already been selected, if yes then replace it

        if (!!selectedFlatFeeApplicationProduct) {
          // Replace the existing product with the selected one
          updatedSelectedProducts = existingSelectedProducts.map((prod) => (prod.ProductUuid === flatFeeApplicationProduct.ProductUuid ? flatFeeProduct : prod));
        } else {
          // Add the selected  product
          updatedSelectedProducts.push(flatFeeProduct);
        }
      }
    } else {
      // Applications have been turned off, remove any app products. Also remove any corequisite products if any were selected
      const productsToRemove = new Set<string>();

      // Firstly, add all the apps products to the removal set
      const allAppsProducts = PricingQuoteSetupObj.IncludedProducts.filter((prod) => prod.ProductType === ProductTypeEnum.Applications);
      allAppsProducts.forEach((appProd) => {
        productsToRemove.add(appProd.ProductUuid);
      });

      // then add any co-requisite products to the removal set
      props.coRequisiteProducts.forEach((coReqProd) => {
        productsToRemove.add(coReqProd.ProductUuid);
      });

      // Filter the list to exclude products with Uuids in the removal set
      updatedSelectedProducts = existingSelectedProducts.filter((prod: PricingQuoteIncludedProductDto) => !productsToRemove.has(prod.ProductUuid));
    }

    dispatch({
      type: buildPricingQuoteDto.type,
      payload: {
        IncludedProducts: updatedSelectedProducts,
      } as PricingQuoteDetailsDto,
    });
  }, [isApplicationsSelected]);

  /**
   * This useffect listens for any changes in our master obj and checks if application products have been added.
   * If it has then it turns on the toggle. Useful when turning off/on any other product causes this product to turn on/off
   */
  useEffect(() => {
    // Check if all applicationProducts are included in IncludedProducts
    const allAppsSelected = availableApplicationProducts.every((appProduct) => PricingQuoteSetupObj.IncludedProducts.some((includedProduct) => includedProduct.ProductUuid === appProduct.ProductUuid));
    // If all application products are selected, turn on the toggle
    if (allAppsSelected) {
      setIsApplicationsSelected(true);
    } else {
      setIsApplicationsSelected(false);
    }
  }, [PricingQuoteSetupObj]);

  return (
    <Accordion sx={{ marginTop: 2 }} elevation={0}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ marginTop: 2, boxShadow: 'none', '& .MuiPaper-root': { boxShadow: 'none' } }}>
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
          <Switch
            checked={isApplicationsSelected}
            onChange={(e) => {
              if (props.isCustomMode) {
                setIsApplicationsSelected(e.target.checked);
              }
            }}
          />
          <Typography sx={{ fontFamily: 'Work Sans !important', fontWeight: 600, fontSize: 'large' }}>Applications</Typography>
        </Box>
      </AccordionSummary>
    </Accordion>
  );
};

export default ApplicationPreferences;
